// frontend/src/portal/components/RequireClient.jsx
import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from "react";
import { useAuth, UserButton, useOrganization, useOrganizationList, useUser, useClerk, useSession } from "@clerk/clerk-react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { fetchPortalMe, fetchSessionBootstrap, setPortalTokenProvider, setPortalTenantSlugProvider, setPortalSignOutCallback } from "../../api.js";
import { getTenantSlug } from "../../lib/tenant.js";
import { clearSentryUserContext, setSentryUserContext } from "../../sentry.js";

const PortalAuthContext = React.createContext(null);

export function usePortalAuth() {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) throw new Error("usePortalAuth must be used within RequireClient");
  return ctx;
}

// ─────────────────────────────────────────────────────────────────────────────
// Auth state machine
// ─────────────────────────────────────────────────────────────────────────────
const AUTH_STATES = {
  INITIALIZING: "INITIALIZING",
  CLERK_LOADING: "CLERK_LOADING",
  UNAUTHENTICATED: "UNAUTHENTICATED",
  SELECTING_ORG: "SELECTING_ORG",
  CONFIGURING_API: "CONFIGURING_API",
  LOADING_PROFILE: "LOADING_PROFILE",
  READY: "READY",
  ERROR: "ERROR",
};

export default function RequireClient() {
  // ═══════════════════════════════════════════════════════════════════════════
  // ALL HOOKS MUST BE CALLED HERE AT THE TOP LEVEL - NO CONDITIONAL RETURNS
  // ═══════════════════════════════════════════════════════════════════════════

  // Clerk hooks
  const { isLoaded: isAuthLoaded, isSignedIn, getToken } = useAuth();
  const { user, isLoaded: isUserLoaded } = useUser();
  const { session, isLoaded: isSessionLoaded } = useSession();
  const { organization, isLoaded: isOrganizationLoaded } = useOrganization();
  const { organizationList, isLoaded: isOrgListLoaded, setActive } = useOrganizationList({ userMemberships: true });
  const { signOut } = useClerk();

  // Router hooks
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ CRITICAL FIX: Use refs to prevent infinite loops
  const bootstrapLoadedRef = useRef(false);
  const loadingInProgressRef = useRef(false);
  const apiConfiguredRef = useRef(false);

  // Local state hooks
  const [authState, setAuthState] = useState(AUTH_STATES.INITIALIZING);
  const [me, setMe] = useState(null);
  const [bootstrap, setBootstrap] = useState(null);
  const [error, setError] = useState(null);
  const [activatingOrgId, setActivatingOrgId] = useState(null);

  // Resolve Clerk template (prod/dev safe)
  const tokenTemplate = "portal";

  const getTokenWithTemplate = useCallback(() => getToken({ template: tokenTemplate }), [getToken, tokenTemplate]);

  // Resolve a tenant slug once Clerk & orgs are ready
  const tenantSlug = useMemo(() => {
    if (!isUserLoaded || !isOrgListLoaded) return null;

    // 1) user public metadata
    const fromMetadata = user?.publicMetadata?.portal?.tenantSlug || user?.publicMetadata?.tenantSlug;
    if (fromMetadata) return String(fromMetadata);

    // 2) current org
    if (organization?.slug) return organization.slug;

    // 3) first org in list
    const firstOrg = Array.isArray(organizationList) ? organizationList[0] : null;
    if (firstOrg?.organization?.slug) return firstOrg.organization.slug;
    if (firstOrg?.slug) return firstOrg.slug;

    // 4) local storage helper
    const stored = getTenantSlug?.();
    return stored || null;
  }, [isUserLoaded, isOrgListLoaded, user, organization, organizationList]);

  // Build organization options
  const orgOptions = useMemo(() => {
    const map = new Map();

    // From user memberships
    if (Array.isArray(user?.organizationMemberships)) {
      user.organizationMemberships.forEach((m) => {
        const org = m.organization || {};
        const id = org.id || m.organizationId;
        if (!id) return;
        map.set(id, {
          id,
          name: org.name || id,
          slug: org.slug || null,
          role: m.role || null,
        });
      });
    }

    // From organizationList (ensures name/slug even if memberships empty)
    if (Array.isArray(organizationList)) {
      organizationList.forEach((o) => {
        const id = o?.id || o?.organization?.id;
        if (!id) return;
        const cur = map.get(id) || { id };
        map.set(id, {
          ...cur,
          id,
          name: o?.name || o?.organization?.name || cur.name || id,
          slug: o?.slug || o?.organization?.slug || cur.slug || null,
          role: cur.role || null,
        });
      });
    }

    return Array.from(map.values());
  }, [user?.organizationMemberships, organizationList]);

  // Selecting org handler
  const handleSelectOrganization = useCallback(
    async (orgId) => {
      if (!setActive || !orgId) return;
      try {
        setActivatingOrgId(orgId);
        await setActive({ organization: orgId });
      } catch (e) {
        setError(e instanceof Error ? e : new Error("Unable to activate organization"));
        setAuthState(AUTH_STATES.ERROR);
      } finally {
        setActivatingOrgId(null);
      }
    },
    [setActive],
  );

  // Load profile callback - WITH CRITICAL GUARDS
  const loadProfile = useCallback(async () => {
    // ✅ CRITICAL: Prevent concurrent loads
    if (loadingInProgressRef.current) {
      console.log("[RequireClient] Load already in progress, skipping");
      return;
    }

    // ✅ CRITICAL: Prevent re-loading if already loaded
    if (bootstrapLoadedRef.current) {
      console.log("[RequireClient] Bootstrap already loaded, skipping");
      return;
    }

    loadingInProgressRef.current = true;
    setError(null);

    try {
      const tk = await getTokenWithTemplate();
      if (!tk) throw new Error("Missing auth token");

      const [boot, profile] = await Promise.all([fetchSessionBootstrap(), fetchPortalMe()]);

      setBootstrap(boot);
      setMe(profile);
      bootstrapLoadedRef.current = true;
      setAuthState(AUTH_STATES.READY);
      console.log("[RequireClient] Bootstrap loaded successfully");
    } catch (e) {
      setBootstrap(null);
      setMe(null);
      const err = e instanceof Error ? e : new Error("Unable to load portal data");
      if (e && typeof e === "object" && "status" in e) err.status = e.status;
      setError(err);
      setAuthState(AUTH_STATES.ERROR);
      console.error("[RequireClient] Failed to load bootstrap:", err);
    } finally {
      loadingInProgressRef.current = false;
    }
  }, [getTokenWithTemplate]);

  // Context value - compute this regardless of render path
  const memberRole = bootstrap?.memberRole || null;
  const permissions = Array.isArray(bootstrap?.permissions) ? [...bootstrap.permissions] : [];
  const suspension = bootstrap?.tenant?.suspension || null;

  const contextValue = useMemo(
    () => ({
      me,
      refresh: loadProfile,
      loading: authState === AUTH_STATES.LOADING_PROFILE || authState === AUTH_STATES.CONFIGURING_API,
      error,
      tenant: bootstrap?.tenant || null,
      memberRole,
      permissions,
      seats: bootstrap?.seats ?? null,
      seatUsage: bootstrap?.seatUsage || null,
      tenantSlug: bootstrap?.tenant?.slug || tenantSlug || getTenantSlug?.() || null,
      suspension: suspension || null,
      bootstrap: bootstrap || null,
    }),
    [me, loadProfile, authState, error, bootstrap, memberRole, permissions, tenantSlug, suspension],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Sentry context sync (tenant + user) - keep in sync with auth state
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const tenantId = bootstrap?.tenant?.id ?? bootstrap?.tenant?.tenantId ?? null;
    const tenantSlugResolved = bootstrap?.tenant?.slug || tenantSlug || null;
    const tenantName = bootstrap?.tenant?.name || null;

    if (authState === AUTH_STATES.READY) {
      const displayName = me?.displayName || [me?.firstName, me?.lastName].filter(Boolean).join(" ").trim() || user?.fullName || user?.username || null;

      setSentryUserContext({
        appName: "portal",
        tenantId,
        tenantSlug: tenantSlugResolved,
        tenantName,
        memberRole,
        clerkId: user?.id || null,
        sessionId: session?.id || null,
        user: {
          id: me?.id || me?.userId || user?.id || null,
          email: me?.email || user?.primaryEmailAddress?.emailAddress || null,
          displayName,
        },
      });
    } else if (authState === AUTH_STATES.UNAUTHENTICATED || authState === AUTH_STATES.ERROR) {
      clearSentryUserContext({ appName: "portal" });
    }
  }, [authState, bootstrap, memberRole, me, session?.id, tenantSlug, user?.id, user?.fullName, user?.username, user?.primaryEmailAddress?.emailAddress]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Phase 1: decide auth state based on Clerk
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Don't transition states if we're already READY or LOADING_PROFILE
    if (authState === AUTH_STATES.READY || authState === AUTH_STATES.LOADING_PROFILE) {
      return;
    }

    // 1) Wait for Clerk itself
    if (!isAuthLoaded) {
      if (authState !== AUTH_STATES.CLERK_LOADING) {
        setAuthState(AUTH_STATES.CLERK_LOADING);
      }
      return;
    }

    // 2) Once Clerk is loaded, if there is NO session → unauthenticated
    if (!isSignedIn || !session) {
      if (authState !== AUTH_STATES.UNAUTHENTICATED) {
        setAuthState(AUTH_STATES.UNAUTHENTICATED);
      }
      return;
    }

    // 3) For signed-in users, wait for user + session + org list
    if (!isUserLoaded || !isSessionLoaded || !isOrgListLoaded) {
      if (authState !== AUTH_STATES.CLERK_LOADING) {
        setAuthState(AUTH_STATES.CLERK_LOADING);
      }
      return;
    }

    // 4) If we still don't have a tenant slug, go to org selection
    if (!tenantSlug) {
      if (authState !== AUTH_STATES.SELECTING_ORG) {
        setAuthState(AUTH_STATES.SELECTING_ORG);
      }
      return;
    }

    // 5) Signed in + tenant ready → configure API (only if not already configured)
    if (!apiConfiguredRef.current && authState !== AUTH_STATES.CONFIGURING_API) {
      setAuthState(AUTH_STATES.CONFIGURING_API);
    }
  }, [isAuthLoaded, isSignedIn, session, isUserLoaded, isSessionLoaded, isOrgListLoaded, tenantSlug, authState]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Phase 1b: redirect unauthenticated users to /portal/login
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (authState !== AUTH_STATES.UNAUTHENTICATED) return;

    // Avoid loops on the login route itself
    if (location.pathname.startsWith("/portal/login")) return;

    const fromPath = (location.pathname || "/portal/overview") + (location.search || "");
    const target = `/portal/login?returnUrl=${encodeURIComponent(fromPath)}`;

    navigate(target, { replace: true });
  }, [authState, location.pathname, location.search, navigate]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Phase 2: configure token/tenant providers (ONE TIME ONLY)
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (authState !== AUTH_STATES.CONFIGURING_API) return;
    if (apiConfiguredRef.current) return; // ✅ CRITICAL: Already configured

    let alive = true;
    (async () => {
      try {
        // Validate we can obtain a token before wiring providers
        const testToken = await getTokenWithTemplate();
        if (!testToken) throw new Error("Clerk token not available");

        setPortalTokenProvider(async () => {
          try {
            return await getTokenWithTemplate();
          } catch {
            return null;
          }
        });

        setPortalTenantSlugProvider(async () => tenantSlug || null);

        setPortalSignOutCallback(async () => {
          console.log("[RequireClient] Sign-out callback triggered");
          await signOut({ redirectUrl: "/portal/login" });
        });

        apiConfiguredRef.current = true; // ✅ CRITICAL: Mark as configured

        if (!alive) return;
        setAuthState(AUTH_STATES.LOADING_PROFILE);
        console.log("[RequireClient] API configured successfully");
      } catch (e) {
        if (!alive) return;
        setError(e instanceof Error ? e : new Error("Failed to configure API"));
        setAuthState(AUTH_STATES.ERROR);
      }
    })();

    return () => {
      alive = false;
    };
  }, [authState, getTokenWithTemplate, tenantSlug, signOut]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Phase 3: load bootstrap + me (ONLY ONCE, ONLY IN LOADING_PROFILE STATE)
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (authState !== AUTH_STATES.LOADING_PROFILE) return;
    if (bootstrapLoadedRef.current) return; // ✅ CRITICAL: Already loaded
    if (loadingInProgressRef.current) return; // ✅ CRITICAL: Already loading

    console.log("[RequireClient] Starting bootstrap load");
    loadProfile();
  }, [authState, loadProfile]);

  // Friendly redirect once READY on /portal
  useEffect(() => {
    if (authState === AUTH_STATES.READY && !error && location.pathname === "/portal") {
      navigate("/portal/overview", { replace: true });
    }
  }, [authState, error, location.pathname, navigate]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ALL HOOKS CALLED ABOVE - NOW WE CAN DO CONDITIONAL RENDERING
  // ═══════════════════════════════════════════════════════════════════════════

  if (authState === AUTH_STATES.CLERK_LOADING || authState === AUTH_STATES.INITIALIZING) {
    return (
      <div className="portal-loading">
        <p>Loading portal…</p>
      </div>
    );
  }

  if (authState === AUTH_STATES.UNAUTHENTICATED) {
    return (
      <div className="portal-loading">
        <p>Redirecting to login…</p>
      </div>
    );
  }

  if (authState === AUTH_STATES.SELECTING_ORG) {
    if (!orgOptions.length) {
      return (
        <div className="portal-error">
          <h2>No organization assigned</h2>
          <p>Your account is not linked to an organization. Please contact support.</p>
          <button type="button" onClick={() => signOut({ redirectUrl: "/portal/login" })}>
            Sign out
          </button>
        </div>
      );
    }

    return (
      <div className="portal-error">
        <h2>Select an organization</h2>
        <ul className="portal-org-list">
          {orgOptions.map((o) => (
            <li key={o.id} className="portal-org-item">
              <div>
                <strong>{o.name}</strong>
                {o.slug ? <span className="portal-org-slug">{o.slug}</span> : null}
                {o.role ? <div className="portal-org-role">Role: {o.role}</div> : null}
              </div>
              <button type="button" disabled={Boolean(activatingOrgId)} onClick={() => handleSelectOrganization(o.id)}>
                {activatingOrgId === o.id ? "Activating…" : "Use organization"}
              </button>
            </li>
          ))}
        </ul>
        <button type="button" onClick={() => signOut({ redirectUrl: "/portal/login" })}>
          Sign out
        </button>
      </div>
    );
  }

  if (authState === AUTH_STATES.CONFIGURING_API || authState === AUTH_STATES.LOADING_PROFILE) {
    return (
      <div className="portal-loading">
        <p>{authState === AUTH_STATES.CONFIGURING_API ? "Configuring…" : "Loading portal…"}</p>
      </div>
    );
  }

  if (authState === AUTH_STATES.ERROR && error) {
    const isAuth = error.status === 401;
    return (
      <div className="portal-error">
        <h2>{isAuth ? "Authentication required" : "Something went wrong"}</h2>
        <p>{error.message || "Unable to load portal data."}</p>
        {isAuth ? (
          <button type="button" onClick={() => signOut({ redirectUrl: "/portal/login" })}>
            Sign in again
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              // Reset all refs on retry
              bootstrapLoadedRef.current = false;
              loadingInProgressRef.current = false;
              apiConfiguredRef.current = false;
              setAuthState(AUTH_STATES.INITIALIZING);
            }}>
            Retry
          </button>
        )}
      </div>
    );
  }

  // Access gate: read role from bootstrap.membership.role FIRST (with fallbacks)
  const allowedPortalRoles = ["client_user", "staff", "reviewer", "admin", "super_admin", "owner"];

  if (!me || !memberRole || !allowedPortalRoles.includes(memberRole)) {
    return (
      <div className="portal-error">
        <h2>Access denied</h2>
        <p>Your account does not have access to the client portal.</p>
      </div>
    );
  }

  // READY - compute display values
  const tenantName = bootstrap?.tenant?.name || null;
  const isSuspended = Boolean(suspension?.isSuspended) || String(bootstrap?.tenant?.status || "").toLowerCase() === "suspended";
  const graceEnds = suspension?.graceEndsAt ? new Date(suspension.graceEndsAt).toLocaleDateString() : null;
  const supportUrl = suspension?.supportUrl || null;
  const canManageUsers = permissions.includes("users.manage");

  return (
    <PortalAuthContext.Provider value={contextValue}>
      <div className="portal-shell">
        <header className="portal-header">
          <div className="portal-brand">
            <Link to="/portal/overview">CBCFX Client Portal</Link>
            {tenantName ? <span className="portal-tenant-name">{tenantName}</span> : null}
          </div>
          <nav className="portal-nav">
            <Link to="/portal/overview">Overview</Link>
            {canManageUsers ? (
              <>
                <Link to="/portal/admin/members">Manage members</Link>
                <Link to="/portal/admin/invite">Invite users</Link>
              </>
            ) : null}
          </nav>
          <div className="portal-user">
            <UserButton afterSignOutUrl="/portal/login" />
          </div>
        </header>

        {isSuspended ? (
          <div className="portal-banner portal-banner--warning">
            <div>
              This organization is currently suspended. Access is limited
              {graceEnds ? ` until ${graceEnds}` : " during this period"}.
            </div>
            {supportUrl ? (
              <a className="portal-banner__action" href={supportUrl} target="_blank" rel="noopener noreferrer">
                Contact support
              </a>
            ) : null}
          </div>
        ) : null}

        <main className="portal-main">
          <Outlet />
        </main>
      </div>
    </PortalAuthContext.Provider>
  );
}
